import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockChats, mockUsers, mockUser } from '../../mockData';
import RatingModal from './RatingModal';
import './Pages.css';

function ChatPage({ isLoggedIn }) {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [newMessage, setNewMessage] = useState('');
  const [chat, setChat] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [isRated, setIsRated] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);
  
  // Find chat and other user
  useEffect(() => {
    const foundChat = mockChats.find(c => c.chatId === chatId);
    if (foundChat) {
      setChat(foundChat);
      setIsRated(foundChat.rated);
      
      // Find other user
      const otherUserId = foundChat.otherUserId;
      const foundUser = mockUsers.find(u => u.id === otherUserId);
      if (foundUser) {
        setOtherUser(foundUser);
      }
    }
  }, [chatId]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chat]);
  
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim() === '') return;
    
    // In a real app, would send to API here
    // For now, just clear the input
    setNewMessage('');
  };
  
  const handleOpenRatingModal = () => {
    setShowRatingModal(true);
  };
  
  const handleCloseRatingModal = () => {
    setShowRatingModal(false);
  };
  
  const handleSubmitRating = (rating, comment) => {
    // In a real app, would send to API here
    console.log(`Rating submitted: ${rating} stars, Comment: ${comment}`);
    
    // Update the local state to reflect that the user has been rated
    setIsRated(true);
    
    // If this was a real app with a backend, we would update the chat data there
    // For the mock data, we'll just update our local state
    if (chat) {
      const updatedChat = { ...chat, rated: true };
      setChat(updatedChat);
      
      // In a real app, this would be replaced with an API call
      const chatIndex = mockChats.findIndex(c => c.chatId === chatId);
      if (chatIndex !== -1) {
        mockChats[chatIndex].rated = true;
      }
    }
    
    setShowRatingModal(false);
  };
  
  // If not logged in or no chat found, don't render anything (redirect will happen)
  if (!isLoggedIn || !chat || !otherUser) {
    return null;
  }
  
  return (
    <div className="page chat-page">
      <div className="chat-container">
        <div className="chat-header">
          <div className="chat-header-info">
            <h2>Chat with {otherUser.username}</h2>
            <p className="chat-listing-info">
              Regarding: <span className="listing-link">{chat.listingTitle}</span>
            </p>
          </div>
          
          {!isRated && (
            <button 
              className="rate-user-button" 
              onClick={handleOpenRatingModal}
            >
              Rate {otherUser.username}
            </button>
          )}
        </div>
        
        <div className="chat-messages">
          {chat.messages.length === 0 ? (
            <div className="empty-chat-message">
              <p>No messages yet. Send a message to start the conversation.</p>
            </div>
          ) : (
            <>
              {chat.messages.map((message, index) => (
                <div 
                  key={index} 
                  className={`message ${message.senderId === mockUser.id ? 'sent' : 'received'}`}
                >
                  <div className="message-content">
                    <p>{message.text}</p>
                    <span className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        
        <form className="chat-input-container" onSubmit={handleSendMessage}>
          <input 
            type="text" 
            className="chat-input" 
            placeholder="Type your message..." 
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
          />
          <button type="submit" className="send-button">Send</button>
        </form>
      </div>
      
      {showRatingModal && (
        <RatingModal 
          username={otherUser.username} 
          onClose={handleCloseRatingModal} 
          onSubmit={handleSubmitRating}
        />
      )}
    </div>
  );
}

export default ChatPage; 